import { useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Avatar, Badge, Button, Drawer, Dropdown, Grid, type MenuProps } from 'antd';
import { LogoutOutlined, MenuOutlined, SettingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';

import { PageErrorBoundary } from './PageErrorBoundary';
import { PersonPane } from './PersonPane';
import { GlobalSearch } from './GlobalSearch';
import {
  groupOf,
  loadOpenGroups,
  saveOpenGroups,
  selectedNavKey,
  visibleNav,
  type AttentionCounts,
} from './navigation';
import { api } from '../api/client';
import { useAuth, usePermissions } from '../auth/store';
import { signOut } from '../auth/signOut';

const { Sider, Header, Content } = Layout;

/** Queue sizes for the sidebar badges (and the dashboard's "Needs attention"). */
export function useAttention(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'console', 'attention'],
    queryFn: async () => (await api.get<AttentionCounts>('/admin/console/attention')).data,
    enabled,
    refetchInterval: 30_000,
  });
}

function Brand() {
  return (
    <div style={{ height: 64, display: 'flex', alignItems: 'center', gap: 10, padding: '0 18px' }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: 'linear-gradient(135deg,#FB3C7F,#FF6B4A)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 800,
        }}
      >
        D
      </div>
      <strong style={{ color: 'white', fontSize: 16 }}>Dude</strong>
    </div>
  );
}

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useAuth((s) => s.profile);
  const { can, role } = usePermissions();
  // Below `lg` the sidebar becomes a slide-in drawer opened from the header.
  const screens = Grid.useBreakpoint();
  const mobile = screens.lg === false;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Visibility is per-PERMISSION, not per-role, so a sub admin sees exactly the
  // pages they were ticked for. A section with nothing visible disappears with
  // its heading rather than leaving an empty label behind.
  const groups = useMemo(() => visibleNav(can, role), [can, role]);
  const selectedKey = selectedNavKey(location.pathname, location.search, groups);

  // Collapsible groups: the admin's own open/closed choice is remembered, and
  // the group holding the current page is always opened.
  const [openKeys, setOpenKeys] = useState<string[]>(() => loadOpenGroups() ?? ['overview', 'queues']);
  const currentGroup = groupOf(selectedKey, groups);
  useEffect(() => {
    if (currentGroup) setOpenKeys((keys) => (keys.includes(currentGroup) ? keys : [...keys, currentGroup]));
  }, [currentGroup]);
  useEffect(() => saveOpenGroups(openKeys), [openKeys]);
  // Leaving a page closes the mobile menu.
  useEffect(() => setDrawerOpen(false), [location.pathname, location.search]);

  // Badges = work waiting in each queue. A queue this admin cannot open comes
  // back null and shows no badge.
  // An agency login has no Dude-team queues and nothing to search (the API
  // returns null counts / no results to it), so neither is shown or polled.
  const isAgency = role === 'agency';
  const attention = useAttention(!isAgency);

  const menuItems: MenuProps['items'] = groups.map((g) => {
    const waiting = g.items.reduce((n, i) => n + ((i.badge && attention.data?.[i.badge]) || 0), 0);
    return {
      key: g.key,
      label:
        waiting > 0 && !openKeys.includes(g.key) ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>{g.label}</span>
            <Badge count={waiting} size="small" />
          </span>
        ) : (
          g.label
        ),
      children: g.items.map((i) => {
        const count = i.badge ? attention.data?.[i.badge] : undefined;
        return {
          key: i.key,
          icon: i.icon,
          label: count ? (
            <Link to={i.to} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span>{i.label}</span>
              <Badge count={count} size="small" title={`${count} waiting`} />
            </Link>
          ) : (
            <Link to={i.to}>{i.label}</Link>
          ),
        };
      }),
    };
  });

  const userMenu: MenuProps['items'] = [
    ...(can('settings.view')
      ? [{ key: 'settings', icon: <SettingOutlined />, label: 'My profile & settings', onClick: () => navigate('/settings') }]
      : []),
    { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: () => { void signOut().then(() => navigate('/login')); } },
  ];

  const menu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={(keys) => setOpenKeys(keys)}
      items={menuItems}
      style={{ background: '#0F0F12', borderRight: 0 }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {mobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={270}
          title={null}
          closable={false}
          rootClassName="admin-nav-drawer"
          styles={{ body: { padding: 0, background: '#0F0F12' } }}
        >
          <Brand />
          {menu}
        </Drawer>
      ) : (
        <Sider
          width={240}
          style={{ background: '#0F0F12', height: '100vh', position: 'sticky', top: 0, overflowY: 'auto' }}
        >
          <Brand />
          {menu}
        </Sider>
      )}
      <Layout>
        <Header
          className="admin-header"
          style={{
            background: '#0A0A0A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '0 24px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          {mobile && (
            <Button
              type="text"
              aria-label="Open menu"
              icon={<MenuOutlined style={{ color: 'white', fontSize: 18 }} />}
              onClick={() => setDrawerOpen(true)}
            />
          )}
          {isAgency ? <div style={{ flex: 1 }} /> : <GlobalSearch />}
          <Dropdown menu={{ items: userMenu }} placement="bottomRight">
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <Avatar style={{ background: 'linear-gradient(135deg,#FB3C7F,#FF6B4A)' }}>
                {profile?.name?.[0] ?? 'A'}
              </Avatar>
              {!mobile && <span style={{ color: 'white', fontWeight: 600 }}>{profile?.name ?? 'Admin'}</span>}
            </div>
          </Dropdown>
        </Header>
        <Content className="admin-content" style={{ padding: 24, background: '#0A0A0A' }}>
          {/* One page crashing must not blank the whole console. */}
          <PageErrorBoundary>
            {/* A clicked listener / user opens beside the page, never away from it. */}
            <PersonPane>
              <Outlet />
            </PersonPane>
          </PageErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
}
